import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const RAW = process.env.REACT_APP_API_BASE || "";
const API_BASE = RAW.replace(/\/+$/, "");
const APIv1 = API_BASE.endsWith("/api") ? API_BASE : `${API_BASE}/api`;

const STATUS_OPTIONS = [
  "",
  "Unverified",
  "Pending",
  "Verified",
  "BadEmail",
  "BadPhone",
  "NoPublicEmail",
  "Closed",
  "NeedsReview",
];

const SORT_OPTIONS = [
  ["unverified", "Unverified First"],
  ["activelistingsdesc", "Highest Active Listings"],
  ["totallistingsdesc", "Highest Total Listings"],
  ["recent", "Recently Updated"],
  ["name", "Dealer Name"],
];

const valueOf = (row, key) => row?.[key] ?? row?.[key[0].toLowerCase() + key.slice(1)];

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
}

function emptyToDash(value) {
  return value ? value : "-";
}

export default function DealerContactReview() {
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem("staffToken") || "", []);
  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    }),
    [token]
  );

  const [filters, setFilters] = useState({
    state: "FL",
    city: "",
    verified: "",
    verificationStatus: "Unverified",
    missingEmail: false,
    missingPhone: false,
    activeOnly: false,
    q: "",
    sort: "activelistingsdesc",
  });
  const [draft, setDraft] = useState(filters);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) navigate("/staff/login", { replace: true });
  }, [token, navigate]);

  const load = async (nextFilters = filters, nextPage = page) => {
    if (!token) return;
    setLoading(true);
    setErr("");
    try {
      const qs = new URLSearchParams({
        page: String(nextPage),
        pageSize: String(pageSize),
        sort: nextFilters.sort || "unverified",
      });

      if (nextFilters.state.trim()) qs.set("state", nextFilters.state.trim());
      if (nextFilters.city.trim()) qs.set("city", nextFilters.city.trim());
      if (nextFilters.verified) qs.set("verified", nextFilters.verified);
      if (nextFilters.verificationStatus) qs.set("verificationStatus", nextFilters.verificationStatus);
      if (nextFilters.missingEmail) qs.set("missingEmail", "true");
      if (nextFilters.missingPhone) qs.set("missingPhone", "true");
      if (nextFilters.activeOnly) qs.set("activeOnly", "true");
      if (nextFilters.q.trim()) qs.set("q", nextFilters.q.trim());

      console.log("Dealer contact review params", qs.toString());

      const res = await fetch(`${APIv1}/staff/dealers/contact-review?${qs.toString()}`, {
        headers: authHeaders,
      });
      if (!res.ok) {
        const message = await res.text().catch(() => "");
        console.error("Dealer Contact Review API failed", {
          status: res.status,
          statusText: res.statusText,
          message,
        });
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error("Could not load dealer contacts.", e);
      setErr("Could not load dealer contacts.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, token]);

  const applyFilters = () => {
    const next = { ...draft };
    setFilters(next);
    setPage(1);
    load(next, 1);
  };

  const resetFilters = () => {
    const next = {
      state: "FL",
      city: "",
      verified: "",
      verificationStatus: "Unverified",
      missingEmail: false,
      missingPhone: false,
      activeOnly: false,
      q: "",
      sort: "activelistingsdesc",
    };
    setDraft(next);
    setFilters(next);
    setPage(1);
    load(next, 1);
  };

  const applySort = (sort) => {
    const next = { ...draft, sort };
    setDraft(next);
    setFilters(next);
    setPage(1);
    load(next, 1);
  };

  const openEdit = (row) => {
    setErr("");
    setNote("");
    setEditing({
      dealerId: valueOf(row, "DealerId"),
      dealerName: valueOf(row, "DealerName") || "",
      phone: valueOf(row, "Phone") || "",
      email: valueOf(row, "Email") || "",
      verified: valueOf(row, "Verified") === true,
      verificationStatus: valueOf(row, "VerificationStatus") || "",
      contactNotes: valueOf(row, "ContactNotes") || "",
    });
  };

  const saveEdit = async (event) => {
    event.preventDefault();
    if (!editing) return;

    setSaving(true);
    setErr("");
    setNote("");
    try {
      const res = await fetch(`${APIv1}/staff/dealers/${editing.dealerId}/contact`, {
        method: "PATCH",
        headers: authHeaders,
        body: JSON.stringify({
          phone: editing.phone,
          email: editing.email,
          verified: editing.verified,
          verificationStatus: editing.verificationStatus,
          contactNotes: editing.contactNotes,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      setNote("Dealer contact updated.");
      setEditing(null);
      await load(filters, page);
    } catch (e) {
      setErr("Could not save dealer contact.");
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="max-w-7xl mx-auto p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Dealer Contact Review</h1>
          <p className="text-sm text-gray-600">
            Verify dealer contact details and prioritize stores with larger inventory.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate("/staff")}
            className="h-10 px-4 text-sm rounded-lg border border-blue-600 bg-blue-50 text-blue-700 hover:bg-blue-100"
          >
            Staff Home
          </button>
          <button
            onClick={() => load(filters, page)}
            className="h-10 px-4 text-sm rounded-lg bg-gray-800 text-white hover:bg-gray-900"
          >
            Refresh
          </button>
        </div>
      </div>

      {err && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {err}
        </div>
      )}
      {note && (
        <div className="mb-3 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-sm text-teal-800">
          {note}
        </div>
      )}

      <div className="bg-white border rounded-xl p-4 mb-4 grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-1">
          <label className="block text-xs text-gray-600 mb-1">State</label>
          <input
            value={draft.state}
            onChange={(e) => setDraft((p) => ({ ...p, state: e.target.value }))}
            className="h-10 w-full border border-gray-300 rounded-lg px-3 text-sm"
            placeholder="FL"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-600 mb-1">City</label>
          <input
            value={draft.city}
            onChange={(e) => setDraft((p) => ({ ...p, city: e.target.value }))}
            className="h-10 w-full border border-gray-300 rounded-lg px-3 text-sm"
            placeholder="Any city"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-600 mb-1">Verified</label>
          <select
            value={draft.verified}
            onChange={(e) => setDraft((p) => ({ ...p, verified: e.target.value }))}
            className="h-10 w-full border border-gray-300 rounded-lg px-3 text-sm bg-white"
          >
            <option value="">All</option>
            <option value="true">Verified</option>
            <option value="false">Unverified</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-600 mb-1">Verification Status</label>
          <select
            value={draft.verificationStatus}
            onChange={(e) => setDraft((p) => ({ ...p, verificationStatus: e.target.value }))}
            className="h-10 w-full border border-gray-300 rounded-lg px-3 text-sm bg-white"
          >
            <option value="">All</option>
            {STATUS_OPTIONS.filter(Boolean).map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-600 mb-1">Sort</label>
          <select
            value={draft.sort}
            onChange={(e) => applySort(e.target.value)}
            className="h-10 w-full border border-gray-300 rounded-lg px-3 text-sm bg-white"
          >
            {SORT_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-3">
          <label className="block text-xs text-gray-600 mb-1">Search</label>
          <input
            value={draft.q}
            onChange={(e) => setDraft((p) => ({ ...p, q: e.target.value }))}
            className="h-10 w-full border border-gray-300 rounded-lg px-3 text-sm"
            placeholder="Dealer, email, phone, city"
          />
        </div>
        <label className="md:col-span-2 flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={draft.missingEmail}
            onChange={(e) => setDraft((p) => ({ ...p, missingEmail: e.target.checked }))}
          />
          Missing Email
        </label>
        <label className="md:col-span-2 flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={draft.missingPhone}
            onChange={(e) => setDraft((p) => ({ ...p, missingPhone: e.target.checked }))}
          />
          Missing Phone
        </label>
        <label className="md:col-span-2 flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={draft.activeOnly}
            onChange={(e) => setDraft((p) => ({ ...p, activeOnly: e.target.checked }))}
          />
          Only Active Dealers
        </label>
        <div className="md:col-span-6 flex justify-end gap-2">
          <button
            onClick={resetFilters}
            className="h-10 px-4 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            Reset
          </button>
          <button
            onClick={applyFilters}
            className="h-10 px-4 text-sm rounded-lg bg-gray-800 text-white hover:bg-gray-900"
          >
            Apply
          </button>
        </div>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        <table className="w-full table-fixed text-xs xl:text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="w-[18%] p-3 text-left">Dealer</th>
              <th className="w-[8%] p-3 text-left">City</th>
              <th className="w-[4%] p-3 text-left">State</th>
              <th className="w-[9%] p-3 text-left">Phone</th>
              <th className="w-[17%] p-3 text-left">Email</th>
              <th className="w-[7%] p-3 text-left">Verified</th>
              <th className="w-[9%] p-3 text-left">Verification Status</th>
              <th className="w-[6%] p-3 text-right">Active Listings</th>
              <th className="w-[6%] p-3 text-right">Total Listings</th>
              <th className="w-[7%] p-3 text-left">Contact Updated</th>
              <th className="w-[9%] p-3 text-left">Updated By</th>
              <th className="w-[5%] p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={12} className="p-6 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={12} className="p-6 text-center text-gray-500">
                  No dealers found
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((row) => {
                const dealerId = valueOf(row, "DealerId");
                const verified = valueOf(row, "Verified") === true;
                return (
                  <tr key={dealerId} className="border-t align-top">
                    <td className="p-3">
                      <div className="font-medium">{emptyToDash(valueOf(row, "DealerName"))}</div>
                      <div className="text-[11px] text-gray-500">
                        {emptyToDash(valueOf(row, "Address"))} {emptyToDash(valueOf(row, "Zip"))}
                      </div>
                    </td>
                    <td className="p-3 truncate" title={valueOf(row, "City") || ""}>
                      {emptyToDash(valueOf(row, "City"))}
                    </td>
                    <td className="p-3">{emptyToDash(valueOf(row, "State"))}</td>
                    <td className="p-3 truncate" title={valueOf(row, "Phone") || ""}>
                      {emptyToDash(valueOf(row, "Phone"))}
                    </td>
                    <td className="p-3 break-all" title={valueOf(row, "Email") || ""}>
                      {emptyToDash(valueOf(row, "Email"))}
                    </td>
                    <td className="p-3">
                      <span
                        className={
                          "inline-block px-2 py-1 rounded-full text-xs border " +
                          (verified
                            ? "bg-green-50 text-green-700 border-green-200"
                            : "bg-yellow-50 text-yellow-800 border-yellow-200")
                        }
                      >
                        {verified ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="p-3 truncate" title={valueOf(row, "VerificationStatus") || ""}>
                      {emptyToDash(valueOf(row, "VerificationStatus"))}
                    </td>
                    <td className="p-3 text-right">{valueOf(row, "ActiveListingCount") || 0}</td>
                    <td className="p-3 text-right">{valueOf(row, "TotalListingCount") || 0}</td>
                    <td className="p-3">{formatDate(valueOf(row, "ContactUpdatedAt"))}</td>
                    <td className="p-3 truncate" title={valueOf(row, "ContactVerifiedBy") || ""}>
                      {emptyToDash(valueOf(row, "ContactVerifiedBy"))}
                    </td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => openEdit(row)}
                        className="h-9 px-3 rounded-lg bg-blue-700 text-white text-sm hover:bg-blue-800"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-600">
          Page {page} of {totalPages} - {total} total
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-2 text-sm rounded-lg bg-gray-800 text-white hover:bg-gray-900 disabled:opacity-50"
          >
            Prev
          </button>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= totalPages}
            className="px-3 py-2 text-sm rounded-lg bg-gray-800 text-white hover:bg-gray-900 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <form onSubmit={saveEdit} className="w-full max-w-2xl rounded-xl bg-white shadow-xl">
            <div className="border-b px-5 py-4">
              <h2 className="text-xl font-semibold">Edit Dealer Contact</h2>
              <p className="text-sm text-gray-600">{editing.dealerName}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5">
              <div>
                <label className="block text-sm mb-1">Phone</label>
                <input
                  value={editing.phone}
                  onChange={(e) => setEditing((p) => ({ ...p, phone: e.target.value }))}
                  className="h-10 w-full border border-gray-300 rounded-lg px-3"
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Email</label>
                <input
                  type="email"
                  value={editing.email}
                  onChange={(e) => setEditing((p) => ({ ...p, email: e.target.value }))}
                  className="h-10 w-full border border-gray-300 rounded-lg px-3"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={editing.verified}
                  onChange={(e) => setEditing((p) => ({ ...p, verified: e.target.checked }))}
                />
                Verified
              </label>
              <div>
                <label className="block text-sm mb-1">Verification Status</label>
                <select
                  value={editing.verificationStatus}
                  onChange={(e) => setEditing((p) => ({ ...p, verificationStatus: e.target.value }))}
                  className="h-10 w-full border border-gray-300 rounded-lg px-3 bg-white"
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status || "blank"} value={status}>
                      {status || "Blank"}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm mb-1">Notes</label>
                <textarea
                  value={editing.contactNotes}
                  onChange={(e) => setEditing((p) => ({ ...p, contactNotes: e.target.value }))}
                  className="min-h-[120px] w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t px-5 py-4">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="h-10 px-4 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="h-10 px-4 rounded-lg bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Contact"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
